import { useContext } from "react";
import { AuthContext } from "../auth-context";
import { ImgSideLeft, ImgSideRight } from "../widget/images";
import IOList from "../widget/io-list";
import IONumber from "../widget/io-number";
import IOPanelWithNavigation from "../widget/io-panel-with-navigation";
import IOText from "../widget/io-text";
import Panel from "../widget/panel";
import TwoColumns from "../widget/two-columns";
import type { ConsultRicket, Patient } from "./_objects-patient";
import ConsultAbstractConclusion from "./blocs/consult-abstract-conclusion";
import ConsultAbstractIntroduction from "./blocs/consult-abstract-introduction";
import { type RelatedElementGeneratorProps } from "./patient-related-element-generator";

export default function ConsultRicketElement(
  props: { patient: Patient } & RelatedElementGeneratorProps<ConsultRicket>
): React.ReactNode {
  const authenticationContext = useContext(AuthContext)!;

  return (
    <IOPanelWithNavigation<ConsultRicket>
      {...props}
      key={`consult_ricket.${props.file.id}`}
      type="consult_ricket"
      apiRootUrl={`/api/patient/${props.patient.id}/consult_ricket`}
      restrictedForWrite="write"
      restrictedForUnlock="manage_archives"
      canBeLocked={true}
      basePath={`${props.parentPath}/consult_ricket`}
      header={
        <>
          <span className="with-image">
            <ImgSideRight />
            {props.file.leg_right}/{props.file.leg_angle_right}
          </span>
          <span className="with-image">
            <ImgSideLeft />
            {props.file.leg_left}/{props.file.leg_angle_left}
          </span>
        </>
      }
    >
      <ConsultAbstractIntroduction file={props.file} patient={props.patient} />
      <TwoColumns>
        <Panel label="Ricket Data">
          <IOList
            name="walking_difficulties"
            value={props.file.walking_difficulties}
            list={authenticationContext.getList("WalkingCapacities")}
          />
          <IOList
            name="pain"
            value={props.file.pain}
            list={authenticationContext.getList("Pain")}
          />
          <IOList
            name="wrist_enlargement"
            value={props.file.wrist_enlargement}
            list={authenticationContext.getList("Eval03")}
          />
          <IOList
            name="rib_heading"
            value={props.file.rib_heading}
            list={authenticationContext.getList("Eval03")}
          />
          <IOText name="xray" label="XRay" value={props.file.xray} />
          <IONumber
            name="imic_distance"
            label="IMIC Distance"
            value={props.file.imic_distance}
          />
        </Panel>
      </TwoColumns>
      <TwoColumns>
        <Panel
          header={
            <>
              <ImgSideRight />
              <span>Right Leg</span>
            </>
          }
        >
          <IOList
            name="leg_right"
            value={props.file.leg_right}
            list={authenticationContext.getList("LegAnalysis")}
          />
          <IONumber name="leg_angle_right" value={props.file.leg_angle_right} />
          <IONumber name="cross_t_right" value={props.file.cross_t_right} />
          <IONumber name="cross_f_right" value={props.file.cross_f_right} />
        </Panel>
        <Panel
          header={
            <>
              <ImgSideLeft />
              <span>Left leg</span>
            </>
          }
        >
          <IOList
            name="leg_left"
            value={props.file.leg_left}
            list={authenticationContext.getList("LegAnalysis")}
          />
          <IONumber name="leg_angle_left" value={props.file.leg_angle_left} />
          <IONumber name="cross_t_left" value={props.file.cross_t_left} />
          <IONumber name="cross_f_left" value={props.file.cross_f_left} />
        </Panel>
      </TwoColumns>
      <ConsultAbstractConclusion file={props.file} />
    </IOPanelWithNavigation>
  );
}
